import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { 
  Drawer, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Collapse, 
  Divider,
  Typography
} from '@material-ui/core';
import { 
  Description, 
  ExpandLess, 
  ExpandMore, 
  Gavel,
  Dashboard as DashboardIcon,
  Assignment,
  TrendingUp,
  Help,
  CheckCircle,
  Assistant
} from '@material-ui/icons';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';

const drawerWidth = 260;

const useStyles = makeStyles((theme) => ({
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'normal',
    position: 'fixed',
  },
  drawerPaper: {
    width: drawerWidth,
    backgroundColor: '#fff', // Background branco
    borderRight: '1px solid var(--ikaro-gray-medium)',
    overflowX: 'hidden',
    position: 'fixed',
    boxShadow: 'var(--ikaro-shadow-light)', // Sombra sutil
    transition: theme.transitions.create('transform', {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.standard,
    }),
  },
  drawerOpen: {
    transform: 'translateX(0)',
  },
  drawerClose: {
    transform: 'translateX(-100%)',
    borderRight: 'none',
  },
  toolbar: theme.mixins.toolbar,
  nested: {
    paddingLeft: theme.spacing(4),
  },
  listItemIcon: {
    minWidth: 40,
    color: 'var(--ikaro-gray-dark)',
  },
  menuText: {
    whiteSpace: 'normal',
    wordWrap: 'break-word',
  },
  // Novo estilo para itens pais - limpo e moderno
  parentMenuItem: {
    margin: '2px 8px',
    borderRadius: '8px',
    transition: 'all 0.2s ease-in-out',
    '& .MuiListItemText-primary': {
      fontWeight: '600',
      fontSize: '0.9rem',
      color: 'var(--ikaro-text-dark)',
      whiteSpace: 'normal',
    },
    '& .MuiListItemIcon-root': {
      color: 'var(--ikaro-gray-dark)',
    },
    '&:hover': {
      backgroundColor: 'var(--ikaro-orange-light)',
      transform: 'translateX(2px)',
      '& .MuiListItemIcon-root': {
        color: 'var(--ikaro-orange-primary)',
      },
    },
  },
  // Submenu items com estilo moderno
  submenuItem: {
    margin: '2px 16px 2px 24px',
    borderRadius: '6px',
    paddingLeft: theme.spacing(3),
    transition: 'all 0.2s ease-in-out',
    '& .MuiListItemText-primary': {
      fontSize: '0.85rem',
      color: 'var(--ikaro-gray-dark)',
      whiteSpace: 'normal',
    },
    '& .MuiListItemIcon-root': {
      color: 'var(--ikaro-gray-medium)',
      minWidth: 35,
    },
    '&:hover': {
      backgroundColor: 'var(--ikaro-orange-light)',
      transform: 'translateX(2px)',
      '& .MuiListItemIcon-root': {
        color: 'var(--ikaro-orange-primary)',
      },
      '& .MuiListItemText-primary': {
        color: 'var(--ikaro-text-dark)',
      },
    },
  },
  // Submenu item ativo - estilo elegante
  submenuItemActive: {
    margin: '2px 16px 2px 24px',
    borderRadius: '6px',
    paddingLeft: theme.spacing(3),
    backgroundColor: 'var(--ikaro-orange-light)',
    boxShadow: 'var(--ikaro-shadow-light)',
    transform: 'translateX(2px)',
    '& .MuiListItemIcon-root': {
      color: 'var(--ikaro-orange-primary)',
      minWidth: 35,
    },
    '& .MuiListItemText-primary': {
      fontWeight: '600',
      fontSize: '0.85rem',
      color: 'var(--ikaro-orange-primary)',
      whiteSpace: 'normal',
    },
  },
  // Dashboard item normal
  dashboardItem: {
    margin: '4px 8px',
    borderRadius: '8px',
    transition: 'all 0.2s ease-in-out',
    '& .MuiListItemText-primary': {
      fontWeight: '600',
      fontSize: '0.9rem',
      color: 'var(--ikaro-text-dark)',
      whiteSpace: 'normal',
    },
    '& .MuiListItemIcon-root': {
      color: 'var(--ikaro-gray-dark)',
    },
    '&:hover': {
      backgroundColor: 'var(--ikaro-orange-light)',
      transform: 'translateX(2px)',
      '& .MuiListItemIcon-root': {
        color: 'var(--ikaro-orange-primary)',
      },
    },
  },
  // Dashboard item ativo
  dashboardItemActive: {
    margin: '4px 8px',
    borderRadius: '8px',
    backgroundColor: 'var(--ikaro-orange-light)',
    boxShadow: 'var(--ikaro-shadow-light)',
    transform: 'translateX(2px)',
    '& .MuiListItemIcon-root': {
      color: 'var(--ikaro-orange-primary)',
    },
    '& .MuiListItemText-primary': {
      fontWeight: '700',
      fontSize: '0.9rem',
      color: 'var(--ikaro-orange-primary)',
      whiteSpace: 'normal',
    },
  },
  expandIcon: {
    color: 'var(--ikaro-gray-dark)',
    transition: 'all 0.2s ease-in-out',
  },
  collapsibleSection: {
    marginBottom: theme.spacing(1),
  },
  // Divider customizado
  customDivider: {
    margin: '12px 16px',
    backgroundColor: 'var(--ikaro-gray-medium)',
    height: '1px',
  },
}));

const SideMenu = ({ open, setOpen }) => {
  const classes = useStyles();
  const location = useLocation();
  const { t } = useTranslation('sidemenu');
  
  // Estado para controlar a abertura de submenus
  const [openMenus, setOpenMenus] = useState({
    processamento: true, // Inicialmente aberto
    criadorPecas: true, // Mudança: agora também inicialmente aberto
    legalAssistant: false // Inicialmente fechado
  });
  
  // Função para alternar a abertura/fechamento de um submenu
  const handleToggleSubmenu = (menu) => {
    setOpenMenus({
      ...openMenus,
      [menu]: !openMenus[menu]
    });
  };
  
  // Função para fechar o drawer após clicar em um item
  const handleMenuItemClick = () => {
    setOpen(false);
  };
  
  // Verificar se um link está ativo
  const isActive = (path) => {
    return location.pathname === path;
  };

  const drawer = (
    <div>
      <div className={classes.toolbar} />
      <Divider className={classes.customDivider} />
      
      <List>
        {/* Dashboard Item */}
        <ListItem
          button
          component={NavLink}
          to="/dashboard"
          className={isActive('/dashboard') ? classes.dashboardItemActive : classes.dashboardItem}
          onClick={handleMenuItemClick}
        >
          <ListItemIcon className={classes.listItemIcon}>
            <DashboardIcon />
          </ListItemIcon>
          <ListItemText 
            primary={t('dashboard')} 
            className={classes.menuText}
          />
        </ListItem>
        
        <Divider className={classes.customDivider} />
        
        {/* Menu de Processamento de Documentos */}
        <div className={classes.collapsibleSection}>
          <ListItem 
            button 
            onClick={() => handleToggleSubmenu('processamento')}
            className={classes.parentMenuItem}
          >
            <ListItemIcon className={classes.listItemIcon}>
              <Description />
            </ListItemIcon>
            <ListItemText 
              primary={
                <Typography variant="subtitle2" className={classes.menuText}>
                  {t('documentProcessing')}
                </Typography>
              } 
            />
            {openMenus.processamento ? 
              <ExpandLess className={classes.expandIcon} /> : 
              <ExpandMore className={classes.expandIcon} />
            }
          </ListItem>
          <Collapse in={openMenus.processamento} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              <ListItem
                button
                component={NavLink}
                to="/processamentos"
                className={isActive('/processamentos') ? classes.submenuItemActive : classes.submenuItem}
                onClick={handleMenuItemClick}
              >
                <ListItemIcon className={classes.listItemIcon}>
                  <Description />
                </ListItemIcon>
                <ListItemText 
                  primary={t('processings')} 
                  className={classes.menuText}
                />
              </ListItem>
            </List>
          </Collapse>
        </div>
        
        <Divider className={classes.customDivider} />
        
        {/* Menu de Criador Inteligente de Peças */}
        <div className={classes.collapsibleSection}>
          <ListItem 
            button 
            onClick={() => handleToggleSubmenu('criadorPecas')}
            className={classes.parentMenuItem}
          >
            <ListItemIcon className={classes.listItemIcon}>
              <Gavel />
            </ListItemIcon>
            <ListItemText 
              primary={
                <Typography variant="subtitle2" className={classes.menuText}>
                  {t('intelligentPieceCreator')}
                </Typography>
              } 
            />
            {openMenus.criadorPecas ? 
              <ExpandLess className={classes.expandIcon} /> : 
              <ExpandMore className={classes.expandIcon} />
            }
          </ListItem>
          <Collapse in={openMenus.criadorPecas} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {/* Petição Inicial - COMENTADO TEMPORARIAMENTE */}
              {/* <ListItem
                button
                className={classes.submenuItem}
                onClick={(e) => {
                  e.preventDefault();
                  // Mock button - não executa nenhuma ação
                }}
              >
                <ListItemIcon className={classes.listItemIcon}>
                  <Assignment />
                </ListItemIcon>
                <ListItemText 
                  primary={t('initialPetition')} 
                  className={classes.menuText}
                />
              </ListItem> */}

              {/* Contestação - mantém funcionalidade original */}
              <ListItem
                button
                component={NavLink}
                to="/contestacoes"
                className={isActive('/contestacoes') ? classes.submenuItemActive : classes.submenuItem}
                onClick={handleMenuItemClick}
              >
                <ListItemIcon className={classes.listItemIcon}>
                  <Gavel />
                </ListItemIcon>
                <ListItemText 
                  primary={t('contestations')} 
                  className={classes.menuText}
                />
              </ListItem>

              {/* Recursos - COMENTADO TEMPORARIAMENTE */}
              {/* <ListItem
                button
                className={classes.submenuItem}
                onClick={(e) => {
                  e.preventDefault();
                  // Mock button - não executa nenhuma ação
                }}
              >
                <ListItemIcon className={classes.listItemIcon}>
                  <TrendingUp />
                </ListItemIcon>
                <ListItemText 
                  primary={t('appeals')} 
                  className={classes.menuText}
                />
              </ListItem> */}

              {/* Embargos de Declaração - COMENTADO TEMPORARIAMENTE */}
              {/* <ListItem
                button
                className={classes.submenuItem}
                onClick={(e) => {
                  e.preventDefault();
                  // Mock button - não executa nenhuma ação
                }}
              >
                <ListItemIcon className={classes.listItemIcon}>
                  <Help />
                </ListItemIcon>
                <ListItemText 
                  primary={t('embargosDeclaration')} 
                  className={classes.menuText}
                />
              </ListItem> */}

              {/* Razões Finais - COMENTADO TEMPORARIAMENTE */}
              {/* <ListItem
                button
                className={classes.submenuItem}
                onClick={(e) => {
                  e.preventDefault();
                  // Mock button - não executa nenhuma ação
                }}
              >
                <ListItemIcon className={classes.listItemIcon}>
                  <CheckCircle />
                </ListItemIcon>
                <ListItemText 
                  primary={t('finalReasons')} 
                  className={classes.menuText}
                />
              </ListItem> */}
            </List>
          </Collapse>
        </div>
        
        {/* <Divider className={classes.customDivider} /> */}
        
        {/* Menu de Legal Assistant - COMENTADO TEMPORARIAMENTE
        <div className={classes.collapsibleSection}>
          <ListItem 
            button 
            onClick={() => handleToggleSubmenu('legalAssistant')}
            className={classes.parentMenuItem}
          >
            <ListItemIcon className={classes.listItemIcon}>
              <Assistant />
            </ListItemIcon>
            <ListItemText 
              primary={
                <Typography variant="subtitle2" className={classes.menuText}>
                  {t('legalAssistant')}
                </Typography>
              } 
            />
            {openMenus.legalAssistant ? 
              <ExpandLess className={classes.expandIcon} /> : 
              <ExpandMore className={classes.expandIcon} />
            }
          </ListItem>
          <Collapse in={openMenus.legalAssistant} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              Futuros itens serão adicionados aqui
            </List>
          </Collapse>
        </div> */}
      </List>
    </div>
  );

  return (
    <Drawer
      variant="permanent"
      className={classes.drawer}
      classes={{
        paper: `${classes.drawerPaper} ${open ? classes.drawerOpen : classes.drawerClose}`,
      }}
    >
      {drawer}
    </Drawer>
  );
};

export default SideMenu; 